// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * React hooks for working with an Embla carousel.
 *
 * - {@link useCarousel} initialises Embla, mirrors its state into React, and
 *   produces the value consumed by `CarouselContext`.
 * - {@link useArrowElement} computes className + disabled state for a single
 *   navigation arrow.
 */

import {
    ArrowElementProps,
    ArrowElementState,
    CarouselContextValue,
    CarouselInstance,
} from "@vanmeter/App/Components/XngCarousel/models";
import { xClassNames } from "@vanmeter/XDL/Utilities";
import type { EmblaCarouselType, EmblaOptionsType, EmblaPluginType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// ─── useCarousel ──────────────────────────────────────────────────────────────

/**
 * Initialise an Embla carousel and expose its reactive state.
 *
 * @param options Embla options (loop, align, axis, …).
 * @param plugins Embla plugins (Autoplay, AutoHeight, …).
 * @returns
 *   - `carouselRef`   — attach to the Embla viewport element.
 *   - `carouselApi`   — the Embla API, or `null` before init.
 *   - `contextValue`  — the value to pass to `CarouselProvider`.
 */
export const useCarousel = (options: EmblaOptionsType = {}, plugins: EmblaPluginType[] = []) => {
    const [carouselRef, carouselApi = null] = useEmblaCarousel(options, plugins);

    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
    const [canScroll, setCanScroll] = useState(true);
    const [paused, setPaused] = useState(false);
    const pausedRef = useRef(false);
    const isHoveredRef = useRef(false);

    const onPlayPause = useCallback(() => {
        const autoplay = carouselApi?.plugins().autoplay;
        if (paused) {
            pausedRef.current = false;
            setPaused(false);
            if (!isHoveredRef.current) {
                autoplay?.play();
            }
        } else {
            pausedRef.current = true;
            setPaused(true);
            autoplay?.stop();
        }
    }, [paused, carouselApi]);

    const updateState = useCallback((api: EmblaCarouselType) => {
        const prev = api.canScrollPrev();
        const next = api.canScrollNext();
        setCanScrollPrev(prev);
        setCanScrollNext(next);
        setSelectedIndex(api.selectedScrollSnap());
        setCanScroll(prev || next);
    }, []);

    const updateSnaps = useCallback((api: EmblaCarouselType) => {
        setScrollSnaps(api.scrollSnapList());
    }, []);

    useEffect(() => {
        if (!carouselApi) return;

        updateSnaps(carouselApi);
        updateState(carouselApi);

        carouselApi.on("select", updateState);
        carouselApi.on("reInit", updateState);
        carouselApi.on("reInit", updateSnaps);

        return () => {
            carouselApi.off("select", updateState);
            carouselApi.off("reInit", updateState);
            carouselApi.off("reInit", updateSnaps);
        };
    }, [carouselApi, updateState, updateSnaps]);

    // XNG-Changes: Manual pause outranks Embla's mouse-leave autoplay resume.
    // `autoplay.stop()` is a no-op during `autoplay:play` because `autoplayActive`
    // is not set yet — defer until after Embla finishes `startAutoplay()`.
    useEffect(() => {
        if (!carouselApi) return;

        const enforceManualPause = () => {
            if (!pausedRef.current) return;
            queueMicrotask(() => {
                if (pausedRef.current) {
                    carouselApi.plugins().autoplay?.stop();
                }
            });
        };

        carouselApi.on("autoplay:timerset", enforceManualPause);

        return () => {
            carouselApi.off("autoplay:timerset", enforceManualPause);
        };
    }, [carouselApi]);

    useEffect(() => {
        if (!carouselApi) return;

        const root = carouselApi.rootNode();
        const handleMouseEnter = () => {
            isHoveredRef.current = true;
        };
        const handleMouseLeave = () => {
            isHoveredRef.current = false;
            if (pausedRef.current) {
                queueMicrotask(() => {
                    if (pausedRef.current) {
                        carouselApi.plugins().autoplay?.stop();
                    }
                });
            }
        };

        root.addEventListener("mouseenter", handleMouseEnter);
        root.addEventListener("mouseleave", handleMouseLeave);

        return () => {
            root.removeEventListener("mouseenter", handleMouseEnter);
            root.removeEventListener("mouseleave", handleMouseLeave);
        };
    }, [carouselApi]);

    const contextValue: CarouselContextValue = {
        carouselApi,
        canScrollPrev,
        canScrollNext,
        selectedIndex,
        scrollSnaps,
        canScroll,
        direction: options.axis === "y" ? "vertical" : "horizontal",
        paused,
        onPlayPause,
    };

    return {
        carouselRef,
        carouselApi,
        contextValue,
    };
};

// ─── useArrowElement ──────────────────────────────────────────────────────────

const DEBOUNCE_MS = 50;

const buildClassName = (arrowType: ArrowElementProps["arrowType"], showOnMobile: boolean, disabled: boolean) =>
    xClassNames.append([
        `arrow-button xng-carousel-${arrowType}`,
        showOnMobile ? "arrow-on-mobile" : "arrow-on-desktop",
        disabled && "carousel-button-disabled",
    ]);

/**
 * Track the enabled/disabled state of a navigation arrow.
 *
 * The hook subscribes to the carousel's `select`, `scroll`, and `reInit`
 * events and produces the matching class name + disabled flag.
 */
export function useArrowElement({ carousel, showOnMobile = false, arrowType }: ArrowElementProps): ArrowElementState {
    const [state, setState] = useState<ArrowElementState>(() => ({
        className: buildClassName(arrowType, showOnMobile, true),
        disabled: true,
    }));

    const updateArrowState = useCallback(
        (instance: CarouselInstance) => {
            const canScroll = arrowType === "next" ? instance.canScrollNext() : instance.canScrollPrev();
            const isDisabled = !canScroll;

            setState({
                className: buildClassName(arrowType, showOnMobile, isDisabled),
                disabled: isDisabled,
            });
        },
        [arrowType, showOnMobile],
    );

    // `useMemo` here is intentional: `useCallback(debounce(...))` would create
    // a fresh debounced function every render, defeating the debounce.
    const debouncedUpdate = useMemo(
        () => debounce((instance: CarouselInstance) => updateArrowState(instance), DEBOUNCE_MS),
        [updateArrowState],
    );

    // Cancel any pending debounced call when the debouncer changes or unmounts.
    useEffect(() => () => debouncedUpdate.cancel(), [debouncedUpdate]);

    useEffect(() => {
        if (!carousel) {
            setState({
                className: buildClassName(arrowType, showOnMobile, true),
                disabled: true,
            });
            return;
        }

        updateArrowState(carousel);

        const handleSelect = () => updateArrowState(carousel);
        const handleScroll = () => debouncedUpdate(carousel);
        const handleReInit = () => updateArrowState(carousel);

        carousel.on("select", handleSelect);
        carousel.on("scroll", handleScroll);
        carousel.on("reInit", handleReInit);

        return () => {
            carousel.off("select", handleSelect);
            carousel.off("scroll", handleScroll);
            carousel.off("reInit", handleReInit);
        };
    }, [carousel, arrowType, showOnMobile, updateArrowState, debouncedUpdate]);

    return state;
}
